import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { Download, Edit, MoreHorizontal, Trash2 } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AudioPreview } from '@/components/ui/audio-preview';
import { truncateFilename } from '@/lib/utils/truncateFilename';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface Recording {
  blob?: Blob; // Optional - for newly recorded audio
  duration: number;
  id: string; // Client-side UUID
  name: string;
  segmentId?: string; // Backend audio_segment ID (if loaded from server)
  timestamp: Date;
  url?: string; // Optional - for S3 audio files
}

export interface RecordingsListRef {
  addRecording: (recording: Recording) => void;
  getRecordings: () => Recording[];
  removeRecording: (id: string) => void;
  renameRecording: (id: string, name: string) => void;
  setRecordings: (recordings: Recording[]) => void;
}

interface RecordingsListProps {
  onDeleteRecording?: (recording: Recording) => void;
  onRecordingsChange?: (count: number) => void;
}

export const RecordingsList = forwardRef<RecordingsListRef, RecordingsListProps>(
  ({ onDeleteRecording, onRecordingsChange }, ref) => {
  const [recordings, setRecordings] = useState<Recording[]>([]);

  useEffect(() => {
    onRecordingsChange?.(recordings.length);
  }, [recordings, onRecordingsChange]);
  const [deletingRecording, setDeletingRecording] = useState<Recording | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [editingRecording, setEditingRecording] = useState<Recording | null>(null);
  const [newName, setNewName] = useState('');
  const [fileExtension, setFileExtension] = useState('');

  // Expose imperative methods via ref. addRecording / setRecordings both
  // de-dup by id so concurrent recovery paths (IndexedDB replay + server
  // segment sync + live recorder callback) can't produce duplicate React keys.
  useImperativeHandle(
    ref,
    () => ({
      getRecordings: () => recordings,
      setRecordings: (newRecordings: Recording[]) => {
        const seen = new Set<string>();
        const deduped = newRecordings.filter((r) => {
          if (seen.has(r.id)) return false;
          seen.add(r.id);
          return true;
        });
        setRecordings(deduped);
      },
      addRecording: (recording: Recording) => {
        setRecordings((prev) =>
          prev.some((r) => r.id === recording.id) ? prev : [...prev, recording],
        );
      },
      removeRecording: (id: string) => {
        setRecordings((prev) => prev.filter((r) => r.id !== id));
      },
      renameRecording: (id: string, name: string) => {
        setRecordings((prev) => prev.map((r) => (r.id === id ? { ...r, name } : r)));
      },
    }),
    [recordings],
  );

  const handleRenameClick = (recording: Recording) => {
      // Separate filename from extension
      const lastDotIndex = recording.name.lastIndexOf('.');
      if (lastDotIndex > 0) {
        const nameWithoutExt = recording.name.slice(0, lastDotIndex);
        const ext = recording.name.slice(lastDotIndex);
        setNewName(nameWithoutExt);
        setFileExtension(ext);
      } else {
        // No extension found
        setNewName(recording.name);
        setFileExtension('');
      }

      setEditingRecording(recording);
      setRenameDialogOpen(true);
    };

    const handleRenameSubmit = () => {
    if (editingRecording && newName.trim()) {
      // Append the extension back to the new name
      const fullName = newName.trim() + fileExtension;
      // Update local state
      setRecordings((prev) => prev.map((r) => (r.id === editingRecording.id ? { ...r, name: fullName } : r)));
      setRenameDialogOpen(false);
      setEditingRecording(null);
      setNewName('');
      setFileExtension('');
    }
  };

    const handleDialogClose = () => {
      setRenameDialogOpen(false);
      setEditingRecording(null);
      setNewName('');
      setFileExtension('');
    };

  const handleDelete = (id: string) => {
    const recording = recordings.find((r) => r.id === id);
    setRecordings((prev) => prev.filter((r) => r.id !== id));
    if (recording) onDeleteRecording?.(recording);
  };

    // Always render component (so ref is attached), but only show UI when there are recordings
    if (recordings.length === 0) {
      return <></>;
    }

    return (
      <>
        <div className="space-y-2">
          <p className="text-sm font-medium">Saved Recordings ({recordings.length})</p>
          {recordings.map((recording) => {
            // Use URL directly if available, otherwise create object URL from blob
            const audioUrl = recording.url || (recording.blob ? URL.createObjectURL(recording.blob) : '');
            const isServerSegment = !!recording.segmentId;

            if (!audioUrl) {
              console.warn(`Recording ${recording.id} has no URL or blob, skipping`);
              return null;
            }

            return (
              <Card key={recording.id} className="p-0">
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex-1 space-y-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2 min-w-0">
                        <p title={recording.name} className="text-sm font-medium truncate">
                          {truncateFilename(recording.name, 30)}
                        </p>
                        {isServerSegment && (
                          <span className="text-xs text-muted-foreground flex-shrink-0">(saved)</span>
                        )}
                      </div>

                      <div className="flex gap-2 ml-4 flex-shrink-0">
                        {recording.blob && !isServerSegment && (
                          <Button asChild variant="outline" size="sm">
                            <a href={audioUrl} download={recording.name} aria-label={`Download a copy of ${recording.name}`}>
                              <Download data-icon="inline-start" />Save a copy
                            </a>
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" type="button" variant="ghost" className="min-h-11 min-w-11" aria-label={`Recording actions for ${recording.name}`}>
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuGroup>
                              <DropdownMenuItem onSelect={() => handleRenameClick(recording)}><Edit />Rename recording</DropdownMenuItem>
                              <DropdownMenuItem variant="destructive" onSelect={() => setDeletingRecording(recording)}><Trash2 />Delete recording</DropdownMenuItem>
                            </DropdownMenuGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <div className="mt-2">
                      <AudioPreview
                        src={audioUrl}
                        onError={(err) => console.error(`Recording ${recording.id} preview error:`, err)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <AlertDialog open={!!deletingRecording} onOpenChange={(open) => { if (!open) setDeletingRecording(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete recording?</AlertDialogTitle>
              <AlertDialogDescription>
                Remove “{deletingRecording?.name}” from this encounter? It will not be included when you submit for processing. Other recordings will remain.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
              <Button type="button" variant="destructive" onClick={() => {
                if (deletingRecording) handleDelete(deletingRecording.id);
                setDeletingRecording(null);
              }}>Delete this recording</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Rename Recording</DialogTitle>
              <DialogDescription>
                Enter a new name for your recording. The file extension will be preserved.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Recording Name</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="name"
                    autoFocus
                    value={newName}
                    className="flex-1"
                    placeholder="Enter recording name"
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleRenameSubmit();
                      }
                    }}
                  />
                  {fileExtension && (
                    <span className="text-sm text-muted-foreground font-mono whitespace-nowrap">{fileExtension}</span>
                  )}
                </div>
                {fileExtension && (
                  <p className="text-xs text-muted-foreground">
                    Full name: {newName || '...'}
                    {fileExtension}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button type="button" disabled={!newName.trim()} onClick={handleRenameSubmit}>
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

RecordingsList.displayName = 'RecordingsList';
